import {
  currencySymbol,
  FINANCING_LABELS,
  FinancingType,
  type DealItem,
  type UpdateDealPayload,
} from '@performance-crm/shared';
import { Loader2 } from 'lucide-react';
import { useEffect, useState, type FormEvent } from 'react';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { useToast } from '@/components/ui/toast-context';
import { ApiRequestError } from '@/lib/api';
import { fmt, toDateInput, todayInTz } from '@/lib/format';
import { useUpdateDeal } from '@/lib/queries';
import { useCompanyTimezone } from '@/lib/useCompanyTimezone';
import { dayInstant } from './undoForm';

interface Form {
  date: string;
  minuteSignedAt: string;
  amountTotal: string;
  amountInitial: string;
  contractMode: '' | FinancingType;
  bank: string;
  contractNumber: string;
  refundNotes: string;
}

const amount = (v: string | null) => (v === null || v === '' ? '' : String(Number(v)));

function formOf(deal: DealItem, tz: string): Form {
  return {
    date: toDateInput(deal.type === 'VENTA' ? deal.finishedAt : deal.startedAt, tz),
    minuteSignedAt: toDateInput(deal.minuteSignedAt, tz),
    amountTotal: amount(deal.amountTotal),
    amountInitial: amount(deal.amountInitial),
    contractMode: deal.contractMode ?? '',
    bank: deal.bank ?? '',
    contractNumber: deal.contractNumber ?? '',
    refundNotes: deal.refundNotes ?? '',
  };
}

/**
 * Corregir una separación o venta ya registrada (Lote 5 · H4, P14, ux-comercial-04) sin anularla: solo lo que se
 * puede corregir (N° de contrato, banco, modalidad, fechas, montos y las notas de la devolución si la hubo). Las
 * unidades y las monedas no se cambian aquí (se anula el proceso y se registra de nuevo): la reserva se corrige en
 * su propia moneda (`initialCurrency`, H2 · P9) y el total de una separación no se corrige a mano porque es la suma
 * de los precios pactados por unidad (H2 · P15); el precio de venta sí, en la venta. Solo viaja lo que cambió; la
 * API deja auditoría y una nota en la actividad del lead. Solo para quien gestiona la empresa.
 */
export function EditDealDialog({
  companyId,
  deal,
  onOpenChange,
}: {
  companyId: string;
  deal: DealItem | null;
  onOpenChange: (o: boolean) => void;
}) {
  const tz = useCompanyTimezone(companyId);
  const mutation = useUpdateDeal();
  const toast = useToast();
  const [form, setForm] = useState<Form | null>(null);
  const [error, setError] = useState<string | null>(null);
  useEffect(() => {
    if (deal) {
      setForm(formOf(deal, tz));
      setError(null);
    }
  }, [deal, tz]);
  if (!deal || !form) return null;
  const isSale = deal.type === 'VENTA';
  const initial = formOf(deal, tz);
  const set = (patch: Partial<Form>) => setForm((f) => (f ? { ...f, ...patch } : f));
  const label = isSale ? 'venta' : 'separación';

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    const input: UpdateDealPayload = {};
    if (form.date && form.date !== initial.date) {
      const at = dayInstant(form.date, tz);
      if (isSale) input.finishedAt = at;
      else input.startedAt = at;
    }
    if (isSale && form.minuteSignedAt !== initial.minuteSignedAt)
      input.minuteSignedAt = form.minuteSignedAt ? dayInstant(form.minuteSignedAt, tz) : null;
    if (isSale && form.amountTotal !== initial.amountTotal)
      input.amountTotal = form.amountTotal === '' ? null : Number(form.amountTotal);
    if (form.amountInitial !== initial.amountInitial)
      input.amountInitial = form.amountInitial === '' ? null : Number(form.amountInitial);
    if (form.contractMode !== initial.contractMode) input.contractMode = form.contractMode || null;
    if (form.bank.trim() !== initial.bank) input.bank = form.bank.trim() || null;
    if (isSale && form.contractNumber.trim() !== initial.contractNumber)
      input.contractNumber = form.contractNumber.trim() || null;
    if (deal.state === 'DEVUELTO' && form.refundNotes.trim() !== initial.refundNotes)
      input.refundNotes = form.refundNotes.trim() || null;
    if (Object.keys(input).length === 0) {
      toast.success('No había nada que corregir');
      onOpenChange(false);
      return;
    }
    try {
      await mutation.mutateAsync({ id: deal.id, input });
      toast.success(`Se corrigió la ${label}`);
      onOpenChange(false);
    } catch (err) {
      setError(err instanceof ApiRequestError ? err.message : 'No se pudo guardar la corrección');
    }
  };

  return (
    <Dialog open={!!deal} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-xl">
        <form onSubmit={submit} className="space-y-4">
          <DialogHeader>
            <DialogTitle>
              Corregir {label}
              {deal.units.length ? ` · ${deal.units.map((u) => u.code).join(', ')}` : ''}
            </DialogTitle>
            <DialogDescription>
              Cambia solo lo que estuvo mal: queda en la auditoría y en la actividad del lead. Las
              unidades y las monedas no se corrigen aquí: si la unidad o la moneda es otra, anula el
              proceso y regístralo de nuevo.
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-3 sm:grid-cols-2">
            <div className="space-y-1">
              <Label htmlFor="ed-date">{isSale ? 'Fecha de venta' : 'Fecha de separación'}</Label>
              <Input
                id="ed-date"
                type="date"
                value={form.date}
                max={todayInTz(tz)}
                onChange={(e) => set({ date: e.target.value })}
                required
              />
            </div>
            {isSale && (
              <div className="space-y-1">
                <Label htmlFor="ed-minute">Fecha de la minuta (opcional)</Label>
                <Input
                  id="ed-minute"
                  type="date"
                  value={form.minuteSignedAt}
                  max={todayInTz(tz)}
                  onChange={(e) => set({ minuteSignedAt: e.target.value })}
                />
              </div>
            )}
            {isSale ? (
              <div className="space-y-1">
                <Label htmlFor="ed-total">Precio total ({currencySymbol(deal.currency)})</Label>
                <Input
                  id="ed-total"
                  type="number"
                  min={0}
                  step="0.01"
                  value={form.amountTotal}
                  onChange={(e) => set({ amountTotal: e.target.value })}
                />
              </div>
            ) : (
              deal.amountTotal !== null && (
                // H2 · P15: el total de la separación es la suma de los precios pactados por unidad; no se escribe.
                <p
                  className="text-muted-foreground text-xs sm:col-span-2"
                  data-testid="ed-sep-total"
                >
                  Total de la separación:{' '}
                  <strong>{fmt.money(deal.amountTotal, deal.currency)}</strong> (suma de los precios
                  de sus unidades; no se corrige a mano: el precio de venta se corrige en la venta).
                </p>
              )
            )}
            <div className="space-y-1">
              <Label htmlFor="ed-initial">
                {isSale ? 'Cuota inicial' : 'Monto de la separación'} (
                {currencySymbol(deal.initialCurrency)})
              </Label>
              <Input
                id="ed-initial"
                type="number"
                min={0}
                step="0.01"
                value={form.amountInitial}
                onChange={(e) => set({ amountInitial: e.target.value })}
              />
            </div>
            <div className="space-y-1">
              <Label htmlFor="ed-mode">Modalidad</Label>
              <Select
                id="ed-mode"
                value={form.contractMode}
                onChange={(e) => set({ contractMode: e.target.value as '' | FinancingType })}
              >
                <option value="">—</option>
                {Object.values(FinancingType).map((v) => (
                  <option key={v} value={v}>
                    {FINANCING_LABELS[v]}
                  </option>
                ))}
              </Select>
            </div>
            <div className="space-y-1">
              <Label htmlFor="ed-bank">Banco</Label>
              <Input
                id="ed-bank"
                value={form.bank}
                maxLength={80}
                onChange={(e) => set({ bank: e.target.value })}
              />
            </div>
            {isSale && (
              <div className="space-y-1">
                <Label htmlFor="ed-contract">N° de contrato</Label>
                <Input
                  id="ed-contract"
                  value={form.contractNumber}
                  maxLength={60}
                  onChange={(e) => set({ contractNumber: e.target.value })}
                  placeholder="Llega después de registrar la venta"
                />
              </div>
            )}
            {deal.state === 'DEVUELTO' && (
              <div className="space-y-1 sm:col-span-2">
                <Label htmlFor="ed-refund-notes">Notas de la devolución</Label>
                <Textarea
                  id="ed-refund-notes"
                  rows={2}
                  value={form.refundNotes}
                  onChange={(e) => set({ refundNotes: e.target.value })}
                />
              </div>
            )}
          </div>
          {error && (
            <p
              role="alert"
              className="bg-destructive/10 text-destructive rounded-md px-3 py-2 text-sm"
            >
              {error}
            </p>
          )}
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Cancelar
            </Button>
            <Button type="submit" disabled={mutation.isPending} data-testid="edit-deal-submit">
              {mutation.isPending && <Loader2 className="h-4 w-4 animate-spin" />} Guardar
              corrección
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
